/**
 * CustomFeeSchedulesSection — Admin → Fees → Custom Fee Schedules tab.
 *
 * Fee Schedule model, 2026-07-01 REDESIGN (decisions/2026-07-01_fee-schedule-model-redesign.md;
 * C-246, supersedes parts of C-244/C-190). A Custom schedule is a named, FIRM-OWNED
 * fee recipe (no owning agreement) that any agreement it is made AVAILABLE to can be
 * assigned. Definition is a DIRECT Admin write (maker-checker retired). Billing
 * frequency + VAT live PER-ACCOUNT (Agreement → Fees → Billing details), not on the
 * schedule.
 *
 * Slice-4 #6 — ONE EDIT TRANSACTION (§3g Pattern A): the whole detail panel
 * (definition + AuM-basis group + availability) is edited together. Enter Edit,
 * change anything, Save commits the batch and returns to Read; Discard exits
 * (dirty-form guarded). The impact-preview gate (loud warning + confirm) fires on
 * definition OR basis edits (both move the rate); availability changes are guarded
 * server-side (revoke / narrowing 409s, surfaced friendly). Create stays a discrete
 * modal (the modal IS its own commit — §3g Pattern B).
 *
 * Sub-editors, all STAGED until Save:
 *   - Definition   — name / description / algorithm / rates / effective-from.
 *   - Basis group  — (tranches only) source agreements (auto-follow their accounts)
 *     + explicit per-account include/exclude overrides. The #4b tri-state tree
 *     replaces the picker UI but keeps this same staged {sources, overrides} model
 *     and the Save-diff logic below.
 *   - Availability — all-agreements flag OR explicit grants.
 *
 * Save diffs each draft against LIVE server state (the react-query caches), so a
 * partial-failure re-Save only retries what failed (§3g corollary 6).
 */
import { useCallback, useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import {
  ActionIcon, Alert, Badge, Button, Checkbox, Group, Modal, NumberInput,
  Paper, Radio, SegmentedControl, Stack, Switch, Table, Text, Textarea, TextInput, Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle, IconArchive, IconArrowBackUp, IconChevronDown, IconChevronRight,
  IconCopy, IconInfoCircle, IconPencil, IconPlus, IconX,
} from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import { agreementsApi } from '../api/agreements';
import {
  bpsToPct, centsToEur, eurToCents, feeSchedulesApi, fmtEurFromCents, pctToBps,
  type BasisCandidateAccount, type BasisOverrideMode, type CustomScheduleCreate,
  type FeeKind, type FeeScheduleOut,
} from '../api/feeSchedules';
import {
  TrancheBandEditor, bandsFromTranches, buildBandsIn, validateBandDrafts,
  type BandDraft,
} from './TrancheBandEditor';
import { AgreementPicker } from './EntityPicker';
import { useDirtyFormGuard } from '../hooks/useDirtyFormGuard';
import { useDirtyForm } from '../components/DirtyFormProvider';
import type { UUID } from '../api/types';

const CUSTOM_KEY = ['fee-schedules', 'custom'] as const;
const BASIS_KEY = (id: UUID) => ['fee-schedules', id, 'basis'] as const;
const AVAIL_KEY = (id: UUID) => ['fee-schedules', id, 'availability'] as const;

const EDIT_IMPACT_WARNING =
  'This schedule is a live reference — editing its definition or basis changes the fee ' +
  'for every account assigned to it, from the next calculation onward (past settled fees ' +
  'are never touched). If you only want to change the fee for SOME accounts, cancel and ' +
  'clone this schedule under a new name instead, then reassign just those accounts.';

// ── labelled reference (a staged source/grant carries its own display label,
// resolved at stage time so the badge renders before Save) ────────────────────
interface LabelledRef { id: UUID; label: string }
interface OverrideDraft { account_id: UUID; agreement_id: UUID | null; agreementLabel: string | null; mode: BasisOverrideMode; label: string }

// ── definition draft ─────────────────────────────────────────────────────────
interface DefDraft {
  name: string;
  description: string;
  kind: FeeKind;
  fixedEur: number | '';
  flatPct: number | '';
  bands: BandDraft[];
  effFrom: string;
}

function seedDef(s?: FeeScheduleOut): DefDraft {
  return {
    name: s?.name ?? '',
    description: s?.description ?? '',
    kind: s?.kind ?? 'flat',
    fixedEur: s?.fixed_amount_cents != null ? centsToEur(s.fixed_amount_cents) : '',
    flatPct: s?.flat_rate_bps != null ? bpsToPct(s.flat_rate_bps) : '',
    bands: bandsFromTranches(s?.tranches),
    effFrom: s?.effective_from ?? '',
  };
}

function validateDef(d: DefDraft): string | null {
  if (!d.name.trim()) return 'Name is required.';
  if (d.kind === 'fixed' && (d.fixedEur === '' || d.fixedEur < 0)) return 'Enter the fixed € amount.';
  if (d.kind === 'flat' && (d.flatPct === '' || d.flatPct < 0 || d.flatPct > 100)) return 'Enter a flat rate between 0 and 100 %.';
  if (d.kind === 'tranches') return validateBandDrafts(d.bands);
  return null;
}

/** The definition payload shared by create (minus agreement_id) and update. */
function buildDefPayload(d: DefDraft): Omit<CustomScheduleCreate, 'agreement_id'> {
  return {
    name: d.name.trim(),
    description: d.description.trim() || null,
    kind: d.kind,
    fixed_amount_cents: d.kind === 'fixed' ? eurToCents(d.fixedEur as number) : null,
    flat_rate_bps: d.kind === 'flat' ? pctToBps(d.flatPct as number) : null,
    bands: d.kind === 'tranches' ? buildBandsIn(d.bands) : null,
    effective_from: d.effFrom || null,
  };
}

function defChanged(schedule: FeeScheduleOut, d: DefDraft): boolean {
  return JSON.stringify(buildDefPayload(d)) !== JSON.stringify(buildDefPayload(seedDef(schedule)));
}

const sortedIds = (xs: string[]) => [...xs].sort();

function bandsSummary(s: FeeScheduleOut): string {
  if (s.kind === 'fixed') return s.fixed_amount_cents != null ? `€${fmtEurFromCents(s.fixed_amount_cents)} fixed` : '—';
  if (s.kind === 'flat') return s.flat_rate_bps != null ? `${bpsToPct(s.flat_rate_bps).toFixed(2)}% flat` : '—';
  return s.tranches.slice().sort((a, b) => a.seq - b.seq)
    .map((t) => `${fmtEurFromCents(t.min_eur_cents)}–${t.max_eur_cents === null ? '∞' : fmtEurFromCents(t.max_eur_cents)}: ${bpsToPct(t.rate_bps).toFixed(2)}%`)
    .join('  ·  ');
}

// ── main section ─────────────────────────────────────────────────────────────
export function CustomFeeSchedulesSection() {
  const q = useQuery({ queryKey: CUSTOM_KEY, queryFn: feeSchedulesApi.listCustom });
  const schedules = q.data ?? [];
  const [selectedId, setSelectedId] = useState<UUID | null>(null);
  const [creating, setCreating] = useState(false);
  // #1 (2026-07-02) row-switch guard: the detail panel holds its own Edit-transaction
  // state, so switching the selected schedule must (a) confirm-discard an unsaved edit,
  // and (b) remount the panel (key=selectedId) so it never shows the previous row's
  // stale draft. panelDirty is lifted from the panel via onDirtyChange.
  const { confirmDiscardAsync } = useDirtyForm();
  const [panelDirty, setPanelDirty] = useState(false);
  const requestSelect = useCallback((id: UUID) => {
    if (!panelDirty || id === selectedId) { setSelectedId(id); return; }
    void confirmDiscardAsync().then((ok) => { if (ok) setSelectedId(id); });
  }, [panelDirty, selectedId, confirmDiscardAsync]);

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Text c="dimmed" size="sm" maw={640}>
          A named, reusable fee recipe an agreement can be assigned. Only Admins define and
          grant availability; any agreement's Billing-details picker only shows schedules made
          available to it. Billing frequency and VAT are set per account on the Agreement's Fees tab.
        </Text>
        <Button leftSection={<IconPlus size={16} />} onClick={() => setCreating(true)}>
          Define new schedule
        </Button>
      </Group>

      {q.isError && <Text c="red">Failed to load Custom fee schedules.</Text>}
      {!q.isLoading && schedules.length === 0 && (
        <Alert color="gray" variant="light">No Custom fee schedules defined yet.</Alert>
      )}
      {schedules.length > 0 && (
        <Table withTableBorder striped highlightOnHover>
          <Table.Thead><Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>Kind / rate</Table.Th>
            <Table.Th w={140}>Available</Table.Th>
          </Table.Tr></Table.Thead>
          <Table.Tbody>
            {schedules.map((s) => (
              <Table.Tr key={s.id} onClick={() => requestSelect(s.id)} style={{ cursor: 'pointer' }}>
                <Table.Td>
                  <Text size="sm" fw={500}>{s.name ?? '(unnamed)'}</Text>
                  {s.description && <Text size="xs" c="dimmed">{s.description}</Text>}
                </Table.Td>
                <Table.Td><Text size="sm" c="dimmed">{bandsSummary(s)}</Text></Table.Td>
                <Table.Td>
                  {s.available_to_all
                    ? <Badge color="verde" variant="light">All agreements</Badge>
                    : <Badge color="gray" variant="light">Granted only</Badge>}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      {selectedId && (
        <ScheduleDetailPanel
          key={selectedId}
          schedule={schedules.find((s) => s.id === selectedId) ?? null}
          onClose={() => setSelectedId(null)}
          onSelect={(id) => setSelectedId(id)}
          onDirtyChange={setPanelDirty}
        />
      )}

      <Modal opened={creating} onClose={() => setCreating(false)} size="lg" centered
        title={<Text fw={600}>Define a Custom Fee Schedule</Text>}>
        <CreateScheduleForm onDone={(created) => { setCreating(false); setSelectedId(created?.id ?? null); }} />
      </Modal>
    </Stack>
  );
}

// ── shared definition fields (create modal + panel edit) ─────────────────────
function ScheduleDefinitionFields({
  d, onChange, disabled,
}: { d: DefDraft; onChange: (patch: Partial<DefDraft>) => void; disabled?: boolean }) {
  return (
    <Stack gap="md">
      <TextInput label="Name" required value={d.name} disabled={disabled}
        onChange={(e) => onChange({ name: e.currentTarget.value })}
        placeholder="e.g. Wilson family — tiered" />
      <Text size="xs" c="dimmed">
        Firm-owned, reusable schedule. Set which agreements may use it under Availability;
        billing frequency and VAT are set per account on the Agreement's Fees tab.
      </Text>
      <Textarea label="Description" value={d.description} autosize minRows={1} disabled={disabled}
        onChange={(e) => onChange({ description: e.currentTarget.value })} />

      <Radio.Group label="Algorithm" value={d.kind} onChange={(v) => onChange({ kind: v as FeeKind })}>
        <Group mt={4}>
          <Radio value="fixed" label="Fixed €" disabled={disabled} />
          <Radio value="flat" label="Flat %" disabled={disabled} />
          <Radio value="tranches" label="Per-tranches" disabled={disabled} />
        </Group>
      </Radio.Group>

      {d.kind === 'fixed' && (
        <NumberInput label="Fixed amount (€/yr)" min={0} thousandSeparator value={d.fixedEur} disabled={disabled}
          onChange={(v) => onChange({ fixedEur: typeof v === 'number' ? v : '' })} w={220}
          description="Bills this amount to EACH account assigned to it — to share a total, define a smaller schedule and assign it to every account." />
      )}
      {d.kind === 'flat' && (
        <NumberInput label="Flat rate (%/yr)" min={0} max={100} step={0.01} decimalScale={2} suffix=" %"
          value={d.flatPct} disabled={disabled}
          onChange={(v) => onChange({ flatPct: typeof v === 'number' ? v : '' })} w={200} />
      )}
      {d.kind === 'tranches' && (
        <Stack gap="xs">
          <Text size="sm" fw={500}>Rate bands (applied to the basis-group's consolidated AuM)</Text>
          <TrancheBandEditor bands={d.bands} onChange={(bands) => onChange({ bands })} />
        </Stack>
      )}

      <TextInput type="date" label="Effective from (optional)" value={d.effFrom} disabled={disabled}
        onChange={(e) => onChange({ effFrom: e.currentTarget.value })} w={220} />
    </Stack>
  );
}

// ── create form (discrete modal — §3g Pattern B, the modal IS the commit) ────
function CreateScheduleForm({ onDone }: { onDone: (s: FeeScheduleOut | null) => void }) {
  const qc = useQueryClient();
  const [d, setD] = useState<DefDraft>(() => seedDef());
  const [saving, setSaving] = useState(false);
  const dirty = Boolean(d.name || d.description);
  const confirmDiscard = useDirtyFormGuard(dirty);
  const patch = (p: Partial<DefDraft>) => setD((prev) => ({ ...prev, ...p }));

  async function create() {
    const err = validateDef(d);
    if (err) { notifications.show({ color: 'red', title: 'Cannot save', message: err }); return; }
    setSaving(true);
    try {
      // Firm-owned: agreement_id omitted (availability is the sole scoping axis).
      const result = await feeSchedulesApi.createCustom(buildDefPayload(d));
      notifications.show({ color: 'green', message: `"${result.name}" defined.` });
      await qc.invalidateQueries({ queryKey: CUSTOM_KEY });
      onDone(result);
    } catch (e) {
      notifications.show({
        color: 'red', title: 'Could not save', autoClose: 8000,
        message: e instanceof ApiError ? e.detail : 'Unknown error',
      });
    } finally { setSaving(false); }
  }

  return (
    <Stack gap="md">
      <ScheduleDefinitionFields d={d} onChange={patch} />
      <Group justify="flex-end">
        <Button variant="default" leftSection={<IconArrowBackUp size={16} />}
          onClick={() => confirmDiscard(() => onDone(null))}>Cancel</Button>
        <Button color="azul" loading={saving} onClick={create}>Define schedule</Button>
      </Group>
    </Stack>
  );
}

// ── detail panel: ONE Edit transaction (definition + basis + availability) ────
function ScheduleDetailPanel({
  schedule, onClose, onSelect, onDirtyChange,
}: { schedule: FeeScheduleOut | null; onClose: () => void; onSelect: (id: UUID) => void; onDirtyChange?: (dirty: boolean) => void }) {
  const qc = useQueryClient();
  const scheduleId = schedule?.id;

  // Basis + availability load in Read too (to render summaries); seeded into
  // drafts on entering Edit.
  const basisQ = useQuery({
    queryKey: scheduleId ? BASIS_KEY(scheduleId) : ['fee-schedules', 'basis', 'none'],
    queryFn: () => feeSchedulesApi.getBasis(scheduleId as UUID),
    enabled: !!scheduleId && schedule?.kind === 'tranches',
  });
  const availQ = useQuery({
    queryKey: scheduleId ? AVAIL_KEY(scheduleId) : ['fee-schedules', 'avail', 'none'],
    queryFn: () => feeSchedulesApi.getAvailability(scheduleId as UUID),
    enabled: !!scheduleId,
  });

  const [editMode, setEditMode] = useState(false);
  const [def, setDef] = useState<DefDraft | null>(null);
  const [sources, setSources] = useState<LabelledRef[]>([]);
  const [overrides, setOverrides] = useState<OverrideDraft[]>([]);
  const [availAll, setAvailAll] = useState(false);
  const [availGrants, setAvailGrants] = useState<LabelledRef[]>([]);
  const [saving, setSaving] = useState(false);
  const [showImpactGate, setShowImpactGate] = useState(false);
  const [impactConfirm, setImpactConfirm] = useState(false);
  // Staged basis/availability picker inputs (declared with the other hooks —
  // above the early `return null` — so hook order is stable, rules-of-hooks).
  const [newGrantId, setNewGrantId] = useState<UUID | null>(null);
  const [resolving, setResolving] = useState(false);
  // #4b basis mode: 'automatic' = empty basis (co-assigned accounts, self-maintaining);
  // 'choose' = the explicit cross-agreement tree (source/override rows). Seeded in enterEdit.
  const [basisMode, setBasisMode] = useState<'automatic' | 'choose'>('automatic');
  const [confirmAuto, setConfirmAuto] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [showDeprecate, setShowDeprecate] = useState(false);
  const [deprecating, setDeprecating] = useState(false);

  // Slice-4 #5: agreements relying on this schedule's 'available to all' umbrella.
  // Fetched in Edit while the flag is ON, so turning it off can auto-stage their
  // grants (safe-by-default — nobody orphaned).
  const dependentsQ = useQuery({
    queryKey: scheduleId ? ['fee-schedules', scheduleId, 'availability', 'dependents'] : ['fee-schedules', 'deps', 'none'],
    queryFn: () => feeSchedulesApi.availabilityDependents(scheduleId as UUID),
    enabled: !!scheduleId && editMode && (availQ.data?.available_to_all ?? schedule?.available_to_all ?? false),
  });

  const isTranches = (def?.kind ?? schedule?.kind) === 'tranches';

  // ── dirty computation (draft vs live server) ──
  const basisDirty = (): boolean => {
    if (!isTranches) return false;
    const srv = basisQ.data;
    const srvSources = sortedIds((srv?.sources ?? []).map((s) => s.agreement_id));
    const drfSources = sortedIds(sources.map((s) => s.id));
    if (JSON.stringify(srvSources) !== JSON.stringify(drfSources)) return true;
    const srvOv = sortedIds((srv?.overrides ?? []).map((o) => `${o.account_id}:${o.mode}`));
    const drfOv = sortedIds(overrides.map((o) => `${o.account_id}:${o.mode}`));
    return JSON.stringify(srvOv) !== JSON.stringify(drfOv);
  };
  const availDirty = (): boolean => {
    const srv = availQ.data;
    if ((srv?.available_to_all ?? schedule?.available_to_all ?? false) !== availAll) return true;
    if (availAll) return false;  // grants are moot under available-to-all — skipped on Save
    const srvGrants = sortedIds((srv?.agreements ?? []).map((a) => a.agreement_id));
    const drfGrants = sortedIds(availGrants.map((a) => a.id));
    return JSON.stringify(srvGrants) !== JSON.stringify(drfGrants);
  };
  const defDirty = (): boolean => !!schedule && !!def && defChanged(schedule, def);
  const dirty = editMode && (defDirty() || basisDirty() || availDirty());
  const confirmDiscard = useDirtyFormGuard(dirty);
  // #1 (2026-07-02): lift dirty to the parent so a row-switch can confirm-discard
  // before changing the selected schedule; report clean on unmount.
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  if (!schedule) return null;
  const sched = schedule; // narrowed for closures below

  const editReady = availQ.isSuccess && (sched.kind !== 'tranches' || basisQ.isSuccess);

  function enterEdit() {
    setDef(seedDef(sched));
    const srcs = basisQ.data?.sources ?? [];
    const ovs = basisQ.data?.overrides ?? [];
    setSources(srcs.map((s) => ({
      id: s.agreement_id, label: s.mnemonic ?? s.code ?? s.agreement_id.slice(0, 8),
    })));
    setOverrides(ovs.map((o) => ({
      account_id: o.account_id, agreement_id: o.agreement_id,
      agreementLabel: o.agreement_mnemonic ?? o.agreement_code ?? null,
      mode: o.mode, label: o.label ?? o.account_id.slice(0, 8),
    })));
    setBasisMode(srcs.length > 0 || ovs.length > 0 ? 'choose' : 'automatic');
    setAvailAll(availQ.data?.available_to_all ?? sched.available_to_all);
    setAvailGrants((availQ.data?.agreements ?? []).map((a) => ({
      id: a.agreement_id, label: a.mnemonic ?? a.code ?? a.agreement_id.slice(0, 8),
    })));
    setEditMode(true);
  }
  function leaveEdit() {
    setDef(null); setSources([]); setOverrides([]); setAvailGrants([]); setEditMode(false);
  }

  // Slice-4 #5 safe-by-default: turning 'available to all' OFF auto-stages a grant
  // for every agreement currently relying on the umbrella, so nobody loses access
  // by default. The admin then prunes any that should no longer use it — and THAT
  // prune is what re-arms the narrowing guard. Turning it back ON leaves the (now
  // moot) grants staged; they're skipped on Save while available-to-all.
  function toggleAvailAll(checked: boolean) {
    if (!checked) {
      const deps = dependentsQ.data ?? [];
      setAvailGrants((prev) => {
        const have = new Set(prev.map((g) => g.id));
        const merged = [...prev];
        for (const d of deps) if (!have.has(d.agreement_id)) {
          merged.push({ id: d.agreement_id, label: d.mnemonic ?? d.code ?? d.agreement_id.slice(0, 8) });
        }
        return merged;
      });
    }
    setAvailAll(checked);
  }

  // ── build the Save op list from the diff (draft vs live server) ──
  function buildOps(): { label: string; run: () => Promise<unknown> }[] {
    const ops: { label: string; run: () => Promise<unknown> }[] = [];
    const id = sched.id;

    if (def && defChanged(sched, def)) {
      const payload = buildDefPayload(def);
      ops.push({ label: 'definition', run: () => feeSchedulesApi.updateCustom(id, payload) });
    }

    if (isTranches) {
      const srv = basisQ.data;
      const srvSourceIds = new Set((srv?.sources ?? []).map((s) => s.agreement_id));
      const drfSourceIds = new Set(sources.map((s) => s.id));
      for (const s of sources) if (!srvSourceIds.has(s.id)) {
        ops.push({ label: `add source ${s.label}`, run: () => feeSchedulesApi.addBasisSource(id, s.id) });
      }
      for (const s of (srv?.sources ?? [])) if (!drfSourceIds.has(s.agreement_id)) {
        ops.push({ label: `remove source`, run: () => feeSchedulesApi.removeBasisSource(id, s.agreement_id) });
      }
      const srvOv = new Map((srv?.overrides ?? []).map((o) => [o.account_id, o.mode]));
      const drfOvIds = new Set(overrides.map((o) => o.account_id));
      for (const o of overrides) if (srvOv.get(o.account_id) !== o.mode) {
        ops.push({ label: `${o.mode} ${o.label}`, run: () => feeSchedulesApi.setBasisOverride(id, o.account_id, o.mode) });
      }
      for (const [accId] of srvOv) if (!drfOvIds.has(accId)) {
        ops.push({ label: `remove override`, run: () => feeSchedulesApi.removeBasisOverride(id, accId) });
      }
    }

    const srvAvail = availQ.data;
    const serverAll = srvAvail?.available_to_all ?? sched.available_to_all;
    const srvGrantIds = new Set((srvAvail?.agreements ?? []).map((a) => a.agreement_id));
    const drfGrantIds = new Set(availGrants.map((a) => a.id));
    // Grants only matter when the schedule is (or is becoming) SPECIFIC. Under
    // available-to-all they're moot — skip them, so a transient toggle-off-then-on
    // that auto-staged grants doesn't create spurious rows.
    if (!availAll) {
      // Grant-adds FIRST so flipping 'all' off never transiently orphans a
      // dependent (the #5 auto-staged grants land before the flag drops).
      for (const g of availGrants) if (!srvGrantIds.has(g.id)) {
        ops.push({ label: `grant ${g.label}`, run: () => feeSchedulesApi.grantAvailability(id, g.id) });
      }
    }
    if (serverAll !== availAll) {
      ops.push({ label: 'availability', run: () => feeSchedulesApi.setAvailableToAll(id, availAll) });
    }
    if (!availAll) {
      for (const a of (srvAvail?.agreements ?? [])) if (!drfGrantIds.has(a.agreement_id)) {
        ops.push({ label: `revoke`, run: () => feeSchedulesApi.revokeAvailability(id, a.agreement_id) });
      }
    }
    return ops;
  }

  async function doSave() {
    const err = def ? validateDef(def) : null;
    if (err) { notifications.show({ color: 'red', title: 'Cannot save', message: err }); return; }

    const ops = buildOps();
    if (ops.length === 0) {
      // Net-zero edit (e.g. a toggle flipped and flipped back) — nothing to
      // persist. Say so, rather than exiting silently (which reads as "did it
      // save?").
      notifications.show({ color: 'gray', title: 'No changes', message: 'Nothing to save — the edit matched the saved values.' });
      leaveEdit();
      return;
    }

    setSaving(true);
    let failures = 0;
    for (const op of ops) {
      try { await op.run(); }
      catch (e) {
        failures += 1;
        notifications.show({
          // Stable id per op → a repeated Save replaces the toast instead of
          // stacking a new one each attempt; auto-dismiss so it needn't be
          // closed by hand (the failed change also stays visibly staged).
          id: `fee-save-err-${op.label}`,
          color: 'red', title: `Could not save: ${op.label}`, autoClose: 8000,
          message: e instanceof ApiError ? e.detail : 'Unknown error',
        });
      }
    }
    // Refetch live state so a re-Save diffs against reality (only failed ops remain).
    await Promise.all([
      qc.invalidateQueries({ queryKey: CUSTOM_KEY }),
      qc.invalidateQueries({ queryKey: BASIS_KEY(sched.id) }),
      qc.invalidateQueries({ queryKey: AVAIL_KEY(sched.id) }),
    ]);
    setSaving(false);

    if (failures === 0) {
      notifications.show({ color: 'green', message: `"${def?.name.trim() || sched.name}" saved.` });
      leaveEdit();
    } else if (ops.length - failures > 0) {
      // Partial success only: name the split. When EVERYTHING failed (e.g. the
      // lone availability-narrowing guard), the per-op "Could not save" message
      // already explains it — no redundant summary toast.
      notifications.show({
        id: 'fee-save-summary',
        color: 'orange', title: 'Saved with errors',
        message: `${ops.length - failures} change(s) saved, ${failures} failed — those stay staged. Fix the cause and Save again.`,
      });
    }
  }

  function onSaveClick() {
    // Rate-moving edits (definition or basis) go through the impact gate;
    // availability-only changes are guarded server-side (revoke / narrowing 409s).
    if (defDirty() || basisDirty()) { setImpactConfirm(false); setShowImpactGate(true); return; }
    void doSave();
  }

  // Clone + deprecate (Slice-4 #8) — discrete Pattern-B actions on the SAVED schedule
  // (not part of the Edit transaction); Read-mode only. Clone is safe (creates a copy);
  // deprecate is consequential → its own confirm modal, 409-if-assigned surfaced friendly.
  async function doClone() {
    setCloning(true);
    try {
      const created = await feeSchedulesApi.cloneCustom(sched.id);
      await qc.invalidateQueries({ queryKey: CUSTOM_KEY });
      notifications.show({ color: 'green', message: `Cloned as "${created.name ?? 'copy'}".` });
      onSelect(created.id);   // jump to the new copy so the user can edit it
    } catch (e) {
      notifications.show({ color: 'red', title: 'Could not clone', autoClose: 8000,
        message: e instanceof ApiError ? e.detail : 'Unknown error' });
    } finally { setCloning(false); }
  }

  async function doDeprecate() {
    setDeprecating(true);
    try {
      await feeSchedulesApi.deprecateCustom(sched.id);
      await qc.invalidateQueries({ queryKey: CUSTOM_KEY });
      notifications.show({ color: 'green', message: `"${sched.name ?? 'Schedule'}" deprecated.` });
      setShowDeprecate(false);
      onClose();
    } catch (e) {
      notifications.show({ color: 'red', title: 'Could not deprecate', autoClose: 8000,
        message: e instanceof ApiError ? e.detail : 'Reassign the accounts using this schedule first.' });
      setShowDeprecate(false);
    } finally { setDeprecating(false); }
  }

  // ── staged basis/availability mutators (label resolved at stage time; the
  // picker-input hooks are declared above with the other useState calls) ──
  async function stageGrant() {
    const grantId = newGrantId;
    if (!grantId) return;
    if (availGrants.some((g) => g.id === grantId)) { setNewGrantId(null); return; }
    setResolving(true);
    try {
      const a = await agreementsApi.get(grantId);
      setAvailGrants((prev) => [...prev, { id: grantId, label: a.client_mnemonic || a.code_1 || grantId.slice(0, 8) }]);
    } catch { setAvailGrants((prev) => [...prev, { id: grantId, label: grantId.slice(0, 8) }]); }
    finally { setResolving(false); setNewGrantId(null); }
  }

  return (
    <Paper withBorder p="lg" radius="md">
      <Stack gap="lg">
        <Group justify="space-between">
          <Title order={4}>{sched.name ?? '(unnamed)'}</Title>
          <Group gap="xs">
            {!editMode ? (
              <>
                <Button size="xs" variant="light" leftSection={<IconPencil size={14} />}
                  onClick={enterEdit} disabled={!editReady}>
                  Edit
                </Button>
                <Button size="xs" variant="default" leftSection={<IconCopy size={14} />}
                  onClick={doClone} loading={cloning}>
                  Clone
                </Button>
                <Button size="xs" variant="subtle" color="red" leftSection={<IconArchive size={14} />}
                  onClick={() => setShowDeprecate(true)}>
                  Deprecate
                </Button>
                <ActionIcon variant="subtle" color="gray" onClick={onClose} aria-label="Close">
                  <IconX size={16} />
                </ActionIcon>
              </>
            ) : (
              <>
                <Button size="xs" variant="default" leftSection={<IconArrowBackUp size={14} />}
                  onClick={() => confirmDiscard(leaveEdit)}>Discard</Button>
                <Button size="xs" color="azul" loading={saving} onClick={onSaveClick}>Save changes</Button>
              </>
            )}
          </Group>
        </Group>

        {/* ── READ ────────────────────────────────────────────────────── */}
        {!editMode && (
          <Stack gap="md">
            <Stack gap={4}>
              {sched.description && <Text size="sm" c="dimmed">{sched.description}</Text>}
              <Text size="sm">{bandsSummary(sched)}</Text>
            </Stack>
            {sched.kind === 'tranches' && (
              <Stack gap={2}>
                <Text size="sm" fw={500}>AuM-basis group</Text>
                <Text size="xs" c="dimmed">
                  Live membership ({basisQ.data?.members.length ?? 0} account{basisQ.data?.members.length === 1 ? '' : 's'}):
                  {' '}{basisQ.data?.members.length
                    ? basisQ.data.members.map((m, i) => `(${i + 1}) ${m.label ?? m.account_id.slice(0, 8)}`).join(', ')
                    : 'none yet'}.
                </Text>
              </Stack>
            )}
            <Stack gap={2}>
              <Text size="sm" fw={500}>Availability</Text>
              {(availQ.data?.available_to_all ?? sched.available_to_all) ? (
                <Badge color="verde" variant="light" w="fit-content">All agreements</Badge>
              ) : (availQ.data?.agreements.length ? (
                <Group gap="xs">
                  {availQ.data.agreements.map((a) => (
                    <Badge key={a.agreement_id} variant="light">{a.mnemonic ?? a.code ?? a.agreement_id.slice(0, 8)}</Badge>
                  ))}
                </Group>
              ) : <Text size="xs" c="dimmed">Granted only — no agreements yet.</Text>)}
            </Stack>
          </Stack>
        )}

        {/* ── EDIT (one transaction) ──────────────────────────────────── */}
        {editMode && def && (
          <Stack gap="lg">
            <ScheduleDefinitionFields d={def} onChange={(p) => setDef((prev) => (prev ? { ...prev, ...p } : prev))} />

            {isTranches && (
              <Stack gap="sm">
                <Title order={5}>AuM-basis group</Title>
                <Text size="xs" c="dimmed">
                  Only used to work out the tiered rate — never to decide what's billed (billing is
                  always on each account's own AuM). Membership recalculates when you Save.
                </Text>
                <SegmentedControl
                  value={basisMode}
                  onChange={(v) => {
                    if (v === 'automatic') {
                      // Confirm before clearing an existing selection (friendly in-app
                      // modal, not window.confirm); an empty selection switches directly.
                      if (sources.length > 0 || overrides.length > 0) { setConfirmAuto(true); return; }
                      setBasisMode('automatic');
                    } else {
                      setBasisMode('choose');
                    }
                  }}
                  data={[
                    { value: 'automatic', label: 'Accounts on this schedule (automatic)' },
                    { value: 'choose', label: 'Choose accounts…' },
                  ]}
                />
                {basisMode === 'automatic' ? (
                  <Alert color="gray" variant="light" icon={<IconInfoCircle size={16} />}>
                    Automatic: the tiered rate is worked out over the accounts assigned this schedule
                    within each agreement, per agreement — self-maintaining, no setup. Switch to
                    "Choose accounts…" only for a fixed cross-agreement (family) basis.
                  </Alert>
                ) : (
                  <BasisTreeEditor
                    sources={sources} setSources={setSources}
                    overrides={overrides} setOverrides={setOverrides}
                  />
                )}
              </Stack>
            )}

            <Stack gap="sm">
              <Title order={5}>Availability</Title>
              <Switch label="Available to all agreements (incl. future)"
                checked={availAll} onChange={(e) => toggleAvailAll(e.currentTarget.checked)} />
              {!availAll && (dependentsQ.data?.length ?? 0) > 0 && (
                <Alert color="azul" variant="light" icon={<IconInfoCircle size={16} />}>
                  Made specific: the {dependentsQ.data!.length} agreement(s) currently using this
                  schedule via "available to all" were kept as explicit grants below, so nobody
                  loses access. Remove any that should no longer use it.
                </Alert>
              )}
              {!availAll && (
                <>
                  <Group align="flex-end" gap="xs">
                    <div style={{ width: 280 }}>
                      <AgreementPicker label="Grant to agreement" placeholder="Add an agreement…"
                        value={newGrantId} onChange={setNewGrantId}
                        statusFilter={['Active', 'Prospect']} /* P1: Prospects get their schedule before promotion */ />
                    </div>
                    <Button size="sm" onClick={stageGrant} disabled={!newGrantId || resolving}>Grant</Button>
                  </Group>
                  {availGrants.length > 0 && (
                    <Group gap="xs">
                      {availGrants.map((a) => (
                        <Badge key={a.id} variant="light" rightSection={
                          <ActionIcon size="xs" variant="transparent"
                            onClick={() => setAvailGrants((prev) => prev.filter((x) => x.id !== a.id))} aria-label="Remove grant">
                            <IconX size={10} />
                          </ActionIcon>
                        }>{a.label}</Badge>
                      ))}
                    </Group>
                  )}
                </>
              )}
            </Stack>
          </Stack>
        )}
      </Stack>

      <Modal opened={showImpactGate} onClose={() => setShowImpactGate(false)} centered
        title={<Text fw={600}>Confirm edit — shared schedule</Text>}>
        <Stack>
          <Alert color="orange" icon={<IconAlertTriangle size={16} />} title="This affects every account on it">
            {EDIT_IMPACT_WARNING}
          </Alert>
          <Checkbox checked={impactConfirm} onChange={(e) => setImpactConfirm(e.currentTarget.checked)}
            label="I have reviewed the impact and confirm this edit." />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setShowImpactGate(false)}>Cancel</Button>
            <Button color="orange" disabled={!impactConfirm} loading={saving}
              onClick={() => { setShowImpactGate(false); void doSave(); }}>
              Save anyway
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={confirmAuto} onClose={() => setConfirmAuto(false)} centered
        title={<Text fw={600}>Switch to automatic?</Text>}>
        <Stack>
          <Text size="sm">
            This clears the chosen source agreements and account include/excludes. The tiered
            rate will instead be worked out automatically over the accounts assigned this schedule
            within each agreement.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmAuto(false)}>Cancel</Button>
            <Button color="orange" onClick={() => {
              setSources([]); setOverrides([]); setBasisMode('automatic'); setConfirmAuto(false);
            }}>Switch to automatic</Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={showDeprecate} onClose={() => setShowDeprecate(false)} centered
        title={<Text fw={600}>Deprecate this schedule?</Text>}>
        <Stack>
          <Alert color="orange" icon={<IconAlertTriangle size={16} />} title="Retire from use">
            Deprecating hides "{sched.name ?? 'this schedule'}" from the assignment pickers and
            availability, keeping it for history and audit — it can't be assigned to new accounts.
            Blocked if any account is still assigned to it (reassign those first).
          </Alert>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setShowDeprecate(false)}>Cancel</Button>
            <Button color="red" loading={deprecating} onClick={doDeprecate}>Deprecate</Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
}

// ── #4b: "Choose accounts" tri-state cross-agreement basis tree ──────────────
// A nicer UI over the SAME staged {sources, overrides} model #6 already batches:
//   check an agreement          → a basis source (all its accounts, auto-following)
//   expand + uncheck an account → an exclude override
//   expand + check an account   → an include override (agreement need not be a source)
// Nodes = source agreements ∪ agreements owning an override ∪ agreements added here.
function BasisTreeEditor({ sources, setSources, overrides, setOverrides }: {
  sources: LabelledRef[];
  setSources: Dispatch<SetStateAction<LabelledRef[]>>;
  overrides: OverrideDraft[];
  setOverrides: Dispatch<SetStateAction<OverrideDraft[]>>;
}) {
  const [addId, setAddId] = useState<UUID | null>(null);
  const [adding, setAdding] = useState(false);
  const [extra, setExtra] = useState<LabelledRef[]>([]);

  const nodeIds = useMemo(() => {
    const seen = new Set<UUID>();
    const out: UUID[] = [];
    const push = (id: UUID) => { if (!seen.has(id)) { seen.add(id); out.push(id); } };
    sources.forEach((s) => push(s.id));
    overrides.forEach((o) => { if (o.agreement_id) push(o.agreement_id); });
    extra.forEach((n) => push(n.id));
    return out;
  }, [sources, overrides, extra]);

  const knownLabels = useMemo(() => {
    const m = new Map<UUID, string>();
    sources.forEach((s) => m.set(s.id, s.label));
    overrides.forEach((o) => { if (o.agreement_id && o.agreementLabel) m.set(o.agreement_id, o.agreementLabel); });
    extra.forEach((n) => m.set(n.id, n.label));
    return m;
  }, [sources, overrides, extra]);

  async function addNode() {
    const aid = addId;
    if (!aid) return;
    if (nodeIds.includes(aid)) { setAddId(null); return; }
    setAdding(true);
    try {
      const a = await agreementsApi.get(aid);
      setExtra((prev) => [...prev, { id: aid, label: a.client_mnemonic || a.code_1 || aid.slice(0, 8) }]);
    } catch { setExtra((prev) => [...prev, { id: aid, label: aid.slice(0, 8) }]); }
    finally { setAdding(false); setAddId(null); }
  }

  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed">
        Check an agreement to include all its accounts in the basis (auto-following future
        attach/detach); expand to include/exclude individual accounts for cross-family cases.
      </Text>
      <Group align="flex-end" gap="xs">
        <div style={{ width: 320 }}>
          <AgreementPicker label="Add an agreement" placeholder="Add an agreement to choose accounts from…"
            value={addId} onChange={setAddId} statusFilter={['Active']} />
        </div>
        <Button size="sm" onClick={addNode} disabled={!addId || adding}>Add</Button>
      </Group>
      {nodeIds.length === 0 ? (
        <Text size="xs" c="dimmed">No agreements chosen yet — add one above to include its accounts.</Text>
      ) : (
        <Stack gap={6}>
          {nodeIds.map((aid) => (
            <BasisAgreementNode key={aid} agreementId={aid} knownLabel={knownLabels.get(aid)}
              sources={sources} setSources={setSources} overrides={overrides} setOverrides={setOverrides} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

function BasisAgreementNode({
  agreementId, knownLabel, sources, setSources, overrides, setOverrides,
}: {
  agreementId: UUID;
  knownLabel: string | undefined;
  sources: LabelledRef[];
  setSources: Dispatch<SetStateAction<LabelledRef[]>>;
  overrides: OverrideDraft[];
  setOverrides: Dispatch<SetStateAction<OverrideDraft[]>>;
}) {
  const [expanded, setExpanded] = useState(false);

  const labelQ = useQuery({
    queryKey: ['agreement-label', agreementId],
    queryFn: () => agreementsApi.get(agreementId),
    enabled: !knownLabel,
    staleTime: 60_000,
  });
  const label = knownLabel
    ?? (labelQ.data ? (labelQ.data.client_mnemonic || labelQ.data.code_1) : agreementId.slice(0, 8));

  const candidatesQ = useQuery({
    queryKey: ['fee-schedules', 'basis-candidates', agreementId],
    queryFn: () => feeSchedulesApi.basisCandidates(agreementId),
    enabled: expanded,
    staleTime: 30_000,
  });

  const isSource = sources.some((s) => s.id === agreementId);
  const mine = overrides.filter((o) => o.agreement_id === agreementId);
  const excludes = new Set(mine.filter((o) => o.mode === 'exclude').map((o) => o.account_id));
  const includes = new Set(mine.filter((o) => o.mode === 'include').map((o) => o.account_id));

  const checked = isSource && excludes.size === 0;
  const indeterminate = (isSource && excludes.size > 0) || (!isSource && includes.size > 0);

  function toggleNode() {
    if (isSource) {
      // → not a source: drop the source + all its account overrides
      setSources((prev) => prev.filter((s) => s.id !== agreementId));
      setOverrides((prev) => prev.filter((o) => o.agreement_id !== agreementId));
    } else {
      // → source: add it + drop now-redundant include overrides (all its accounts are in)
      setSources((prev) => [...prev, { id: agreementId, label }]);
      setOverrides((prev) => prev.filter((o) => !(o.agreement_id === agreementId && o.mode === 'include')));
    }
  }

  function toggleAccount(acc: BasisCandidateAccount) {
    const accId = acc.account_id;
    const mode: BasisOverrideMode = isSource ? 'exclude' : 'include';
    // Under a source: an account is in by default → an override means EXCLUDE it.
    // Outside a source: an account is out by default → an override means INCLUDE it.
    const already = isSource ? excludes.has(accId) : includes.has(accId);
    setOverrides((prev) => {
      const rest = prev.filter((o) => o.account_id !== accId);
      return already ? rest : [...rest, { account_id: accId, agreement_id: agreementId, agreementLabel: label, mode, label: acc.label }];
    });
  }

  return (
    <Paper withBorder p="xs" radius="sm">
      <Group gap="xs" wrap="nowrap">
        <ActionIcon variant="subtle" color="gray" size="sm" onClick={() => setExpanded((e) => !e)}
          aria-label={expanded ? 'Collapse' : 'Expand'}>
          {expanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
        </ActionIcon>
        <Checkbox checked={checked} indeterminate={indeterminate} onChange={toggleNode}
          label={<Text size="sm" fw={500}>{label}</Text>} />
        {isSource && <Badge size="xs" variant="light" color="azul">source</Badge>}
      </Group>
      {expanded && (
        <Stack gap={2} pl={40} pt={6}>
          {candidatesQ.isLoading && <Text size="xs" c="dimmed">Loading accounts…</Text>}
          {candidatesQ.data && candidatesQ.data.length === 0 && (
            <Text size="xs" c="dimmed">No accounts on this agreement.</Text>
          )}
          {candidatesQ.data?.map((acc) => {
            const accChecked = isSource ? !excludes.has(acc.account_id) : includes.has(acc.account_id);
            return (
              <Checkbox key={acc.account_id} size="xs" checked={accChecked}
                onChange={() => toggleAccount(acc)}
                label={<Text size="xs">{acc.label}{acc.status !== 'Active' ? ` · ${acc.status}` : ''}</Text>} />
            );
          })}
        </Stack>
      )}
    </Paper>
  );
}
